import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { api } from "./api";
import CustomSelect from "./CustomSelect";

// Index 0 = Monday, matching the server's days_of_week convention.
const DAY_PILLS = ["M", "T", "W", "T", "F", "S", "S"];
const DAY_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
// -1 = last day of the month (matches the server's days_of_month convention).
const MONTH_DAYS = [...Array.from({ length: 31 }, (_, i) => i + 1), -1];

/** Create/edit form for a scheduled task. `task` = null for create. */
export default function ScheduledTaskModal({
  task, agents, models, onClose, onSave,
  defaultCategory = "personal", isTopManager = false, mcpServers = [],
}) {
  const [name, setName] = useState(task?.name || "");
  const [message, setMessage] = useState(task?.message || "");
  const [days, setDays] = useState(() => new Set(task?.days_of_week || []));
  // "weekly" shows the original M–S pills; the other kinds live behind the
  // "More options" toggle so the default form looks unchanged.
  const [kind, setKind] = useState(task?.schedule_kind || "weekly");
  const [monthDays, setMonthDays] = useState(() => new Set(task?.days_of_month || []));
  const [weekOfMonth, setWeekOfMonth] = useState(task?.week_of_month ?? 1);
  const [weekday, setWeekday] = useState(task?.weekday ?? 0);
  const [time, setTime] = useState(
    task ? `${String(task.hour).padStart(2, "0")}:${String(task.minute).padStart(2, "0")}`
         : "09:00");
  const [agentId, setAgentId] = useState(task?.agent_id || agents[0]?._id || "");
  const defaultModel = models.find((m) => m.is_default)?.id || models[0]?.id || "";
  const [model, setModel] = useState(task?.model || defaultModel);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [skills, setSkills] = useState([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState(
    () => new Set(task?.skill_ids || []));
  // Category is chosen at creation only; non-managers always create personal.
  const [category, setCategory] = useState(
    task ? (task.category || "personal") : (isTopManager ? defaultCategory : "personal"));
  const [requiredMcp, setRequiredMcp] = useState(
    () => new Set(task?.required_mcp_servers || []));

  const toggleRequiredMcp = (url) =>
    setRequiredMcp((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });

  useEffect(() => {
    let alive = true;
    api.listSkills().then((rows) => { if (alive) setSkills(rows || []); })
       .catch(() => { if (alive) setSkills([]); });
    return () => { alive = false; };
  }, []);

  const toggleSkill = (id) =>
    setSelectedSkillIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleDay = (d) =>
    setDays((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });

  const toggleMonthDay = (d) =>
    setMonthDays((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });

  const scheduleOk = kind === "weekly" ? days.size > 0
                   : kind === "monthly" ? monthDays.size > 0 : true;
  const canSave = name.trim() && message.trim() && scheduleOk && time;
  const scheduleFields = kind === "weekly"
    ? { days_of_week: [...days].sort((a, b) => a - b) }
    : kind === "monthly"
    ? { days_of_month: [...monthDays].sort((a, b) => a - b) }
    : { week_of_month: weekOfMonth, weekday };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const [hh, mm] = time.split(":").map(Number);
    try {
      await onSave({
        name: name.trim(),
        message: message.trim(),
        schedule_kind: kind,
        ...scheduleFields,
        hour: hh,
        minute: mm,
        agent_id: agentId || undefined,
        model,
        skill_ids: [...selectedSkillIds],
        required_mcp_servers: [...requiredMcp],
        ...(task ? {} : { category }),
      });
    } catch (e2) {
      setErr(e2.message || "Failed to save task");
      setBusy(false);
    }
  };

  // ponytail: portal to body — this panel lives in .ca-section-body (max-height
  // 360px, overflow-y auto), which breaks the fixed backdrop once the task list
  // makes it scroll. Same reason the other modals mount at .ca-chat level.
  return createPortal(
    <div className="ca-mcp-modal-backdrop" onClick={onClose}>
      <form className="ca-mcp-modal ca-sched-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>{task ? "Edit scheduled task" : "New scheduled task"}</h3>
        {isTopManager && !task && (
          <div className="ca-sched-days" role="group" aria-label="Category">
            {[["personal", "Personal"], ["lt", "LT"]].map(([value, label]) => (
              <button key={value} type="button"
                      className={`ca-sched-day${category === value ? " on" : ""}`}
                      aria-pressed={category === value}
                      onClick={() => setCategory(value)}>
                {label}
              </button>
            ))}
          </div>
        )}
        <label>
          Name
          <input className="ca-input" value={name} autoFocus
                 onChange={(e) => setName(e.target.value)}
                 placeholder="Daily Linear digest" />
        </label>
        <label>
          Message
          <textarea className="ca-input ca-sched-textarea" rows={4} value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Summarize what changed in Linear since yesterday…" />
        </label>
        <div className="ca-sched-skills">
          <div className="ca-sched-skills-label">Skills for this task</div>
          {["personal", "role", "global"].map((scope) => {
            const group = skills.filter((s) => (s.scope || "personal") === scope);
            if (group.length === 0) return null;
            const TITLE = { personal: "Personal", role: "Role", global: "Global" };
            return (
              <div key={scope} className="ca-sched-skill-group">
                <div className="ca-sched-skill-group-title">{TITLE[scope]}</div>
                {group.map((s) => (
                  <label key={s._id} className="ca-sched-skill-row">
                    <input type="checkbox"
                           checked={selectedSkillIds.has(s._id)}
                           onChange={() => toggleSkill(s._id)} />
                    <span>{s.name}</span>
                  </label>
                ))}
              </div>
            );
          })}
          {skills.length === 0 && (
            <div className="ca-sched-skill-empty">No skills available.</div>
          )}
          <div className="ca-sched-skill-hint">None selected = no skills run.</div>
        </div>
        <div className="ca-sched-skills">
          <div className="ca-sched-skills-label">Required connectors</div>
          {mcpServers.map((s) => (
            <div key={s.url} className="ca-sched-skill-group">
              <label className="ca-sched-skill-row">
                <input type="checkbox"
                       checked={requiredMcp.has(s.url)}
                       onChange={() => toggleRequiredMcp(s.url)} />
                <span>{s.name}</span>
              </label>
            </div>
          ))}
          {mcpServers.length === 0 && (
            <div className="ca-sched-skill-empty">No connectors available.</div>
          )}
          <div className="ca-sched-skill-hint">
            The task can only be turned on by users who have these connected.
          </div>
        </div>
        {kind === "weekly" && (
          <div className="ca-sched-days" role="group" aria-label="Days of week (Mon–Sun)">
            {DAY_PILLS.map((label, d) => (
              <button key={d} type="button"
                      className={`ca-sched-day${days.has(d) ? " on" : ""}`}
                      aria-pressed={days.has(d)}
                      onClick={() => toggleDay(d)}>
                {label}
              </button>
            ))}
          </div>
        )}
        {kind !== "weekly" && (
          <div className="ca-sched-days" role="group" aria-label="Monthly schedule type">
            {[["monthly", "Day of month"], ["monthly_weekday", "Weekday of month"]].map(([value, label]) => (
              <button key={value} type="button"
                      className={`ca-sched-day${kind === value ? " on" : ""}`}
                      aria-pressed={kind === value}
                      onClick={() => setKind(value)}>
                {label}
              </button>
            ))}
          </div>
        )}
        {kind === "monthly" && (
          <div className="ca-sched-days ca-sched-month-grid" role="group" aria-label="Days of month">
            {MONTH_DAYS.map((d) => (
              <button key={d} type="button"
                      className={`ca-sched-day${monthDays.has(d) ? " on" : ""}`}
                      aria-pressed={monthDays.has(d)}
                      onClick={() => toggleMonthDay(d)}>
                {d === -1 ? "Last" : d}
              </button>
            ))}
          </div>
        )}
        {kind === "monthly_weekday" && (
          <div className="ca-sched-days">
            <CustomSelect value={weekOfMonth} onChange={setWeekOfMonth} ariaLabel="Week of month"
                          options={[[1, "First"], [2, "Second"], [3, "Third"], [4, "Fourth"], [-1, "Last"]]
                            .map(([value, label]) => ({ value, label }))} />
            <CustomSelect value={weekday} onChange={setWeekday} ariaLabel="Weekday"
                          options={DAY_LONG.map((label, value) => ({ value, label }))} />
          </div>
        )}
        <button type="button" className="ca-sched-kind-toggle"
                onClick={() => setKind(kind === "weekly" ? "monthly" : "weekly")}>
          {kind === "weekly" ? "More options (monthly)" : "Back to weekly"}
        </button>
        <label>
          Time (Arizona)
          <input className="ca-input" type="time" value={time}
                 onChange={(e) => setTime(e.target.value)} />
        </label>
        <label>
          Agent
          <CustomSelect
            value={agentId}
            options={agents.map((a) => ({ value: a._id, label: a.name }))}
            onChange={setAgentId}
            ariaLabel="Agent"
          />
        </label>
        <label>
          Model
          <CustomSelect
            value={model}
            options={models.map((m) => ({ value: m.id, label: m.label }))}
            onChange={setModel}
            ariaLabel="Model"
          />
        </label>
        {err && <div className="ca-mcp-modal-err">{err}</div>}
        <div className="ca-mcp-modal-actions">
          <button type="button" className="ca-btn ca-btn--sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="ca-btn ca-btn--sm ca-btn--primary"
                  disabled={busy || !canSave}>
            {busy ? "Saving…" : task ? "Save" : "Create"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
